/**
 * Pure Legion member-grade derivation logic (REV8 spec) — mirrors `lib/links.ts`'s role for the
 * Link Skills page: `LegionProgressService` already computes `effectiveGrade` server-side per
 * class; this module's job is display-only helpers (effect text resolution, the summary strip's
 * totals) on top of it.
 *
 * The grade-threshold math (`gradeFromLevel`) mirrors
 * `backend/.../legion/LegionGradeBrackets.java` exactly (the two languages can't share a literal
 * source, so keep them in sync by hand — see that file's own comment) and is kept here purely so
 * the derivation rule has one client-side unit-tested home, the same way `bracketFromLevel` does
 * for links; the page itself always trusts the server's own `derivedGrade`/`effectiveGrade`.
 *
 * Scoping note (REV8 spec "Rules"): no grid/coin/artifact modeling. Doc 01 §5 records that the
 * Tetris Synergy Grid and Legion Raid are being replaced (KMS Overdrive -> GMS ~fall/winter
 * 2026); grades-per-member is the durable core this page tracks, not the grid.
 */
import type { ClassDto } from './reference';

export type LegionGrade = 'NONE' | 'B' | 'A' | 'S' | 'SS' | 'SSS';

export const GRADE_ORDER: readonly LegionGrade[] = ['NONE', 'B', 'A', 'S', 'SS', 'SSS'];

export interface LegionGradeDto {
  classSlug: string;
  storedGrade: string;
  derivedGrade: string;
  effectiveGrade: string;
  derivedFromCharacterName: string | null;
  derivedFromLevel: number | null;
}

export function rankOfGrade(grade: string): number {
  const index = GRADE_ORDER.indexOf(grade as LegionGrade);
  return index < 0 ? 0 : index;
}

const STANDARD_THRESHOLDS: [number, LegionGrade][] = [
  [60, 'B'],
  [100, 'A'],
  [140, 'S'],
  [200, 'SS'],
  [250, 'SSS'],
];

// Zero exception (doc 01 §5: "Zero uses shifted brackets (130+/160+/180+)") — see the backend
// LegionGradeBrackets doc comment for the SS/SSS-unshifted judgment call.
const ZERO_THRESHOLDS: [number, LegionGrade][] = [
  [130, 'B'],
  [160, 'A'],
  [180, 'S'],
  [200, 'SS'],
  [250, 'SSS'],
];

/** The grade a rostered character's level implies as a floor — same thresholds as the backend. */
export function gradeFromLevel(level: number, isZero: boolean): LegionGrade {
  const thresholds = isZero ? ZERO_THRESHOLDS : STANDARD_THRESHOLDS;
  let grade: LegionGrade = 'NONE';
  for (const [minLevel, candidate] of thresholds) {
    if (level >= minLevel) {
      grade = candidate;
    }
  }
  return grade;
}

/**
 * The row's effect text: the seeded `legionEffect` (11 research-verified specials) where
 * present, otherwise the research-backed generic scaling text keyed to the class's own
 * `mainStat` — compound stats (e.g. Xenon's "STR/DEX/LUK") render verbatim, never rebased to a
 * single stat. `mainStat` is null for a handful of unverified early-seed classes; the fallback
 * omits the stat name rather than inventing one.
 */
export function legionEffectText(klass: ClassDto): string {
  if (klass.legionEffect) {
    return klass.legionEffect.effectText;
  }
  const stat = klass.mainStat ? `${klass.mainStat} ` : '';
  return `${stat}+10 → +100 (B → SSS)`;
}

export interface LegionTotals {
  /** Count of classes whose effective grade is not NONE. */
  memberCount: number;
  /** Count of classes at the max grade (SSS). */
  sssCount: number;
  /**
   * Approx. total legion level: sum of the levels of the account's characters at Lv >= 60,
   * capped to the highest 42 (doc 01 §5: "Total level counts your 42 highest characters"). Marked
   * "approx." on the page because the in-game 2nd-job requirement for sub-100 characters isn't
   * modeled here.
   */
  totalLegionLevel: number;
}

/** Sum of the highest-42 levels among characters at Lv >= 60 — the summary strip's headline stat. */
export function totalLegionLevel(characters: readonly { level: number }[]): number {
  return characters
    .map((c) => c.level)
    .filter((level) => level >= 60)
    .sort((a, b) => b - a)
    .slice(0, 42)
    .reduce((sum, level) => sum + level, 0);
}

/** The `/legion` page's summary strip: member count, SSS count, and the approx. total level. */
export function accountLegionTotals(
  classes: readonly ClassDto[],
  progress: readonly LegionGradeDto[],
  characters: readonly { level: number }[],
): LegionTotals {
  const effectiveByClass = new Map(progress.map((p) => [p.classSlug, p.effectiveGrade]));
  let memberCount = 0;
  let sssCount = 0;
  for (const klass of classes) {
    const grade = effectiveByClass.get(klass.slug) ?? 'NONE';
    if (grade !== 'NONE') {
      memberCount++;
    }
    if (grade === 'SSS') {
      sssCount++;
    }
  }
  return { memberCount, sssCount, totalLegionLevel: totalLegionLevel(characters) };
}
